import { useMemo } from 'react';
import { SolidStatusPill } from '@odo/shared-ui';
import { usePatientVirtualAppointments } from '../../../hooks/useVirtualAppointments';
import { partitionOpenVirtualAppointments } from '../../virtual-appointments/openVirtualAppointments';

// Hover lives on the pill (group-hover) because the pill, not the button, paints the background.
const CHIP_TONE =
    'border border-green-200 bg-green-50 text-green-700 transition-colors group-hover:bg-green-100';
const CHIP_DOT = 'bg-green-600';

interface OpenVirtualAppointmentsChipProps {
    patientId: string;
    onClick: () => void;
}

function openVirtualAppointmentsLabel(count: number): string {
    return `${count} open VA${count === 1 ? '' : 's'}`;
}

// Shares the VAs tab's query; skips its image resolution, which a one-line chip doesn't need.
export function OpenVirtualAppointmentsChip({
    patientId,
    onClick
}: OpenVirtualAppointmentsChipProps) {
    const { data } = usePatientVirtualAppointments(patientId);
    const openCount = useMemo(
        () => partitionOpenVirtualAppointments(data ?? []).open.length,
        [data]
    );
    if (openCount === 0) return null;

    const label = openVirtualAppointmentsLabel(openCount);
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={`Show virtual appointments (${label})`}
            className="group inline-flex rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
        >
            <SolidStatusPill
                value={label}
                label={label}
                toneClassName={CHIP_TONE}
                dotClassName={CHIP_DOT}
            />
        </button>
    );
}
